import React__default from 'react';
import { determineDataTestIdPrefix, getBaseProps, getAriaProps } from '../../../helpers/propHelpers.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { PortalWrapper } from '../Portal/PortalWrapper.js';

const Tooltip = (props) => {
    var _a, _b, _c, _d, _e;
    const [isVisible, setIsVisible] = React__default.useState((props === null || props === void 0 ? void 0 : props.isVisible) || false);
    // needed for PortalTooltip
    const [coords, setCoords] = React__default.useState({});
    const spanRef = React__default.useRef(null);
    const portalElement = document.getElementById(props.portalElementId) || document.createElement('div');
    const tooltipPosition = (_a = props === null || props === void 0 ? void 0 : props.position) !== null && _a !== void 0 ? _a : 'bottom';
    const description = (props === null || props === void 0 ? void 0 : props.customDescriptionTemplate) ? props === null || props === void 0 ? void 0 : props.customDescriptionTemplate : props === null || props === void 0 ? void 0 : props.description;
    if (props.reactContainer) {
        (_c = (_b = props.reactContainer) === null || _b === void 0 ? void 0 : _b.classList) === null || _c === void 0 ? void 0 : _c.add('Custom-tooltip');
    }
    const getTooltipPositionClass = (position) => {
        const positionOptions = [
            'top',
            'bottom',
            'left',
            'right',
            'left-start',
            'right-start',
            'left-end',
            'right-end',
            'top-start',
            'top-end',
            'bottom-start',
            'bottom-end',
        ];
        const modifier = positionOptions.find((po) => po === position) || 'bottom';
        return `Tooltip--${modifier}`;
    };
    const getPortalTooltipPosition = (rect) => {
        const default_bottom = {
            left: rect.left,
            top: rect.top + window.scrollY + rect.height,
        };
        switch (tooltipPosition) {
            case 'bottom':
                return default_bottom;
            case 'top':
                return {
                    left: rect.left,
                    top: rect.top + window.scrollY,
                };
            case 'right':
                return {
                    left: rect.right,
                    top: rect.top + window.scrollY,
                };
            case 'left':
                return {
                    left: rect.left,
                    top: rect.top + window.scrollY,
                };
            default:
                return default_bottom;
        }
    };
    const determineTypeStyles = () => {
        const tooltipType = props.portalElementId ? 'PortalTooltip' : 'Tooltip';
        return (props === null || props === void 0 ? void 0 : props.isFancy)
            ? (props === null || props === void 0 ? void 0 : props.topBorderColor)
                ? `${tooltipType}--fancy ${tooltipType}--${props.topBorderColor}`
                : `${tooltipType}--fancy ${tooltipType}--info`
            : '';
    };
    const handleClick = (e) => {
        if (props === null || props === void 0 ? void 0 : props.showTooltipOnClick) {
            setIsVisible(!isVisible);
        }
        else if (e.key != ' ') {
            // need to do this so the target element doesnt gain focus on click
            e.preventDefault();
        }
    };
    const handleKeyUp = (e) => {
        // handle the escape and spacebar keys
        if ((e.key == 'Escape' && isVisible) || e.key == ' ') {
            handleClick(e);
        }
        if (e.key == 'Enter') {
            setIsVisible(!isVisible);
        }
    };
    const handleFocus = () => {
        if (!(props === null || props === void 0 ? void 0 : props.showTooltipOnClick)) {
            setIsVisible(true);
        }
    };
    const tooltipClass = `${(props === null || props === void 0 ? void 0 : props.showTooltipOnClick) ? 'Tooltip--onclick' : 'Tooltip--hover'} ${isVisible || (props === null || props === void 0 ? void 0 : props.isVisible) ? 'Tooltip--visible' : ''}`;
    const dataTestId = determineDataTestIdPrefix(props, `${props.portalElementId ? 'portal-tooltip' : 'tooltip'}`);
    const baseProps = getBaseProps(props);
    const ariaProps = getAriaProps(props);
    const getTooltipClassNames = () => {
        let classNames = `${determineTypeStyles()} ${tooltipClass}`;
        if (!props.reactContainer) {
            classNames = `Tooltip ${getTooltipPositionClass(props === null || props === void 0 ? void 0 : props.position)} ${classNames}`;
        }
        return `${classNames}`;
    };
    const showPortalTooltip = (is_visible) => {
        var _a, _b;
        const rect = (_b = (_a = spanRef === null || spanRef === void 0 ? void 0 : spanRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) !== null && _b !== void 0 ? _b : null;
        if (rect) {
            setCoords(getPortalTooltipPosition(rect));
            setIsVisible(is_visible);
        }
    };
    const PortalTooltip = portalElement && (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("span", { role: 'button', ref: spanRef, onMouseOver: () => !(props === null || props === void 0 ? void 0 : props.showTooltipOnClick) && showPortalTooltip(true), onMouseOut: () => setIsVisible(false), onFocus: handleFocus, onBlur: () => setIsVisible(false), onMouseDown: (e) => handleClick(e), onKeyUp: (e) => handleKeyUp(e), tabIndex: (_d = props === null || props === void 0 ? void 0 : props.tabIndex) !== null && _d !== void 0 ? _d : 0, "aria-describedby": props['aria-describedby'], "aria-label": props['aria-label'], className: 'Tooltip--target' }, props === null || props === void 0 ? void 0 : props.children),
        React__default.createElement(PortalWrapper, { domNode: portalElement }, !!description && isVisible && (React__default.createElement("div", { className: 'PortalTooltipWrapper', "data-testid": dataTestId },
            React__default.createElement("div", Object.assign({}, ariaProps, { className: `${'PortalTooltip'} ${determineTypeStyles()} ${`PortalTooltip--${tooltipPosition}`}`, role: 'tooltip', "data-testid": `${dataTestId}-main`, style: coords }),
                React__default.createElement("div", { className: `PortalTooltip__content` },
                    ((props === null || props === void 0 ? void 0 : props.headlineIcon) || (props === null || props === void 0 ? void 0 : props.headline)) && (React__default.createElement("h2", { className: `${(props === null || props === void 0 ? void 0 : props.headline) && 'PortalTooltip__header'} ${(props === null || props === void 0 ? void 0 : props.isFancy) && 'PortalTooltip__header-isFancy'}`, "data-testid": `${dataTestId}-headline` },
                        (props === null || props === void 0 ? void 0 : props.headlineIcon) && (React__default.createElement(FontAwesomeIcon, { icon: props === null || props === void 0 ? void 0 : props.headlineIcon.icon, className: `PortalTooltip__icon`, "data-testid": `${dataTestId}-headline-icon` })), props === null || props === void 0 ? void 0 :
                        props.headline)),
                    React__default.createElement("div", { "data-testid": `${dataTestId}-body`, className: 'PortalTooltip__body' }, description))))))));
    return (React__default.createElement(React__default.Fragment, null, props.portalElementId ? (PortalTooltip) : (React__default.createElement("span", { className: `${((props === null || props === void 0 ? void 0 : props.description) || props.customDescriptionTemplate || props.value) && 'Tooltip-wrapper'}`, onBlur: () => setIsVisible(false) },
        React__default.createElement("span", { role: 'button', tabIndex: (_e = props === null || props === void 0 ? void 0 : props.tabIndex) !== null && _e !== void 0 ? _e : 0, className: `Tooltip--target`, onFocus: handleFocus, onMouseDown: (e) => handleClick(e), onKeyUp: (e) => handleKeyUp(e), "aria-describedby": props['aria-describedby'], "aria-label": props['aria-label'] }, props === null || props === void 0 ? void 0 : props.children),
        React__default.createElement("div", Object.assign({}, baseProps, ariaProps, { "data-testid": `${dataTestId}`, className: classNames(getTooltipClassNames(), props.className), role: 'tooltip', id: props['aria-describedby'] || (props === null || props === void 0 ? void 0 : props.id) }),
            React__default.createElement("div", { className: `Tooltip__content` },
                ((props === null || props === void 0 ? void 0 : props.headlineIcon) || (props === null || props === void 0 ? void 0 : props.headline)) && (React__default.createElement("h2", { className: `${(props === null || props === void 0 ? void 0 : props.headline) && 'Tooltip__header'} ${(props === null || props === void 0 ? void 0 : props.isFancy) && 'Tooltip__header-isFancy'}`, "data-testid": `${dataTestId}-headline` },
                    (props === null || props === void 0 ? void 0 : props.headlineIcon) && (React__default.createElement(FontAwesomeIcon, { icon: props === null || props === void 0 ? void 0 : props.headlineIcon.icon, "data-testid": `${dataTestId}-headline-icon`, className: `Tooltip__icon` })), props === null || props === void 0 ? void 0 :
                    props.headline)),
                React__default.createElement("div", { "data-testid": `${dataTestId}-body`, className: 'Tooltip__body' }, (props === null || props === void 0 ? void 0 : props.customDescriptionTemplate)
                    ? props === null || props === void 0 ? void 0 : props.customDescriptionTemplate
                    : (props === null || props === void 0 ? void 0 : props.description)
                        ? props.description
                        : props === null || props === void 0 ? void 0 : props.value)))))));
};

export { Tooltip, Tooltip as default };
//# sourceMappingURL=Tooltip.js.map
